/**
 * Holiday package helpers: pax (people per sharing type), search <-> URL,
 * readers for the search / detail / quote / booking payloads, results filters
 * and sorts, and the book / enquiry request builders.
 *
 * Pure functions only — no React, no network.
 */

import {
  PACKAGE_AGE_LIMITS,
  PACKAGE_LIMITS,
} from '../constants/packageConstants';
import { isAdminOrAdminStaff } from '../../../shared/helper-utils/appUtils';

// ---- small helpers ---------------------------------------------------------

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pad2 = (n) => String(n).padStart(2, '0');

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "2026-12-01" (or "2026-12-01T10:00") -> local Date at midnight; null if unreadable. */
export const fromIsoDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const toIsoDate = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
        date.getDate(),
      )}`
    : '';

export const addDays = (date, days) => {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
};

export const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * When the balance must be paid: `daysBefore` days before departure, but
 * never a date that has already passed — once the window is over the
 * balance is due today. null when it can't be worked out.
 */
export const balanceDueDate = (
  departure,
  daysBefore,
  today = startOfToday(),
) => {
  const days = Number(daysBefore);
  if (!(departure instanceof Date) || !Number.isFinite(days) || days <= 0)
    return null;
  const due = addDays(departure, -days);
  return due < today ? today : due;
};

/** Date -> "01 Dec 2026"; withDay adds the weekday: "Tue, 01 Dec 2026". */
export const formatDate = (date, withDay = false) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const text = `${pad2(date.getDate())} ${
    MONTHS[date.getMonth()]
  } ${date.getFullYear()}`;
  return withDay ? `${WEEKDAYS[date.getDay()]}, ${text}` : text;
};

/** "2026-12-01" -> "01 Dec 2026"; the raw value when it can't be read. */
export const formatIsoDate = (value) => {
  const d = fromIsoDate(value);
  return d ? formatDate(d) : String(value || '');
};

/** "2026-10-16T16:19:13" -> "16 Oct 2026, 16:19" (time as sent). */
export const formatIsoDateTime = (value) => {
  const s = String(value || '');
  const time = /[ T](\d{2}:\d{2})/.exec(s)?.[1];
  return [formatIsoDate(s.slice(0, 10)), time].filter(Boolean).join(', ');
};

/** "2026-12" -> "Dec 2026". */
export const formatMonthKey = (key) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(key || ''));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : String(key || '');
};

export const formatPrice = (amount, currency = 'INR') => {
  const n = num(amount);
  if (n === null) return '';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(n);
  } catch (e) {
    return `${currency || 'INR'} ${Math.round(n)}`;
  }
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

export const daysNightsText = (days, nights) =>
  days == null
    ? ''
    : `${plural(days, 'Day', 'Days')} / ${plural(
        nights ?? 0,
        'Night',
        'Nights',
      )}`;

// ---- pax -------------------------------------------------------------------
// The API counts PEOPLE per sharing type: { twin: 2 } is two adults sharing
// one twin room, { triple: 3 } three adults in one triple room. Twin and
// triple therefore move in whole rooms (2 / 3 people at a time).

export const PACKAGE_PAX_TYPES = [
  {
    key: 'twin',
    step: 2,
    max: 10,
    kind: 'ADULT',
    label: 'Twin sharing',
    hint: 'Adults, 2 per room',
  },
  {
    key: 'single',
    step: 1,
    max: 6,
    kind: 'ADULT',
    label: 'Single occupancy',
    hint: 'Adult, own room',
  },
  {
    key: 'triple',
    step: 3,
    max: 9,
    kind: 'ADULT',
    label: 'Triple sharing',
    hint: 'Adults, 3 per room',
  },
  {
    key: 'child_with_bed',
    step: 1,
    max: 6,
    kind: 'CHILD',
    label: 'Child with bed',
    hint: '2 - 11 years',
  },
  {
    key: 'child_no_bed',
    step: 1,
    max: 6,
    kind: 'CHILD',
    label: 'Child without bed',
    hint: '2 - 11 years',
  },
  {
    key: 'infant',
    step: 1,
    max: 4,
    kind: 'INFANT',
    label: 'Infant',
    hint: 'Under 2 years',
  },
];

export const paxTypeLabel = (key) =>
  PACKAGE_PAX_TYPES.find((p) => p.key === key)?.label || key || '';

export const DEFAULT_PACKAGE_PAX = Object.freeze({
  twin: 2,
  single: 0,
  triple: 0,
  child_with_bed: 0,
  child_no_bed: 0,
  infant: 0,
});

const countOf = (pax, kind) =>
  PACKAGE_PAX_TYPES.filter((p) => p.kind === kind).reduce(
    (sum, p) => sum + (Number(pax?.[p.key]) || 0),
    0,
  );

export const adultsOf = (pax) => countOf(pax, 'ADULT');
export const childrenOf = (pax) => countOf(pax, 'CHILD');
export const infantsOf = (pax) => countOf(pax, 'INFANT');
export const totalPaxOf = (pax) =>
  adultsOf(pax) + childrenOf(pax) + infantsOf(pax);
/** Infants travel on a lap: they take no seat on the departure. */
export const seatsOf = (pax) => adultsOf(pax) + childrenOf(pax);

/** Every sharing type present, zeros included — a safe copy of anything pax-like. */
export const normalizePax = (pax) =>
  PACKAGE_PAX_TYPES.reduce((acc, p) => {
    const raw = Math.max(
      0,
      Math.min(p.max, Number.parseInt(pax?.[p.key], 10) || 0),
    );
    acc[p.key] = raw - (raw % p.step); // twin / triple in whole rooms
    return acc;
  }, {});

/** Only the non-zero sharing types — what the API is sent. */
export const apiPax = (pax) =>
  Object.fromEntries(
    Object.entries(normalizePax(pax)).filter(([, n]) => n > 0),
  );

/** '' when the mix can be booked, otherwise why not. */
export const paxError = (pax) => {
  if (adultsOf(pax) < 1) return 'Add at least one adult.';
  if (infantsOf(pax) > adultsOf(pax))
    return 'Each infant must travel with an adult.';
  if (totalPaxOf(pax) > PACKAGE_LIMITS.MAX_PAX)
    return `A booking can have at most ${PACKAGE_LIMITS.MAX_PAX} travellers.`;
  return '';
};

/** "2 Adults · 1 Child" */
export const paxSummary = (pax) =>
  [
    plural(adultsOf(pax), 'Adult', 'Adults'),
    childrenOf(pax) > 0 && plural(childrenOf(pax), 'Child', 'Children'),
    infantsOf(pax) > 0 && plural(infantsOf(pax), 'Infant', 'Infants'),
  ]
    .filter(Boolean)
    .join(' · ');

/** ['ADULT','ADULT','CHILD','INFANT'] — one passenger form per person, in this order. */
export const passengerTypesFor = (pax) => [
  ...Array(adultsOf(pax)).fill('ADULT'),
  ...Array(childrenOf(pax)).fill('CHILD'),
  ...Array(infantsOf(pax)).fill('INFANT'),
];

export const PASSENGER_TYPE_LABEL = {
  ADULT: 'Adult',
  CHILD: 'Child',
  INFANT: 'Infant',
};

// ---- search <-> URL ----------------------------------------------------------

const URL_PAX = {
  twin: 'tw',
  single: 'sg',
  triple: 'tr',
  child_with_bed: 'cwb',
  child_no_bed: 'cnb',
  infant: 'inf',
};

const clampInt = (value, min, max, fallback) => {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

/** state: { destination, minDays, maxDays, maxPrice, pax } */
export const buildPackageSearchQuery = (state) => {
  const p = new URLSearchParams();
  p.set('dest', String(state.destination || '').trim());
  if (state.minDays) p.set('minD', String(state.minDays));
  if (state.maxDays) p.set('maxD', String(state.maxDays));
  if (state.maxPrice) p.set('maxP', String(state.maxPrice));
  const pax = normalizePax(state.pax);
  Object.entries(URL_PAX).forEach(([key, short]) => {
    if (pax[key]) p.set(short, String(pax[key]));
  });
  return p.toString();
};

export const parsePackageSearchQuery = (search) => {
  const p = new URLSearchParams(search);
  const minDays = clampInt(
    p.get('minD'),
    PACKAGE_LIMITS.MIN_DAYS,
    PACKAGE_LIMITS.MAX_DAYS,
    null,
  );
  const maxDays = clampInt(
    p.get('maxD'),
    PACKAGE_LIMITS.MIN_DAYS,
    PACKAGE_LIMITS.MAX_DAYS,
    null,
  );
  const maxPrice = clampInt(p.get('maxP'), 1, 100000000, null);
  const anyPax = Object.values(URL_PAX).some((short) => p.has(short));
  const pax = anyPax
    ? normalizePax(
        Object.fromEntries(
          Object.entries(URL_PAX).map(([key, short]) => [key, p.get(short)]),
        ),
      )
    : { ...DEFAULT_PACKAGE_PAX };
  return {
    destination: String(p.get('dest') || '')
      .trim()
      .slice(0, 80),
    minDays,
    // a reversed range from a hand-edited URL is read the right way round
    maxDays: minDays && maxDays && maxDays < minDays ? minDays : maxDays,
    maxPrice,
    pax,
  };
};

export const isPackageSearchValid = (s) =>
  Boolean(s?.destination) && !paxError(s?.pax);

/** The search body, in the shape of the API collection (0 / '' = no filter). */
export const buildPackageSearchRequest = (s) => ({
  destination: s.destination,
  query: '',
  minDays: s.minDays || 0,
  maxDays: s.maxDays || 0,
  maxPrice: s.maxPrice || 0,
  month: '',
  sort: '',
  pax: apiPax(s.pax),
  searchId: '',
});

/** "dubai" and "Dubai" are the same destination. */
export const sameDestination = (a, b) =>
  String(a || '')
    .trim()
    .toLowerCase() ===
  String(b || '')
    .trim()
    .toLowerCase();

/** "dubai" -> "Dubai"; anything already mixed-case is left as the supplier wrote it. */
export const prettyDestination = (label) => {
  const s = String(label || '').trim();
  if (!s || s !== s.toLowerCase()) return s;
  return s.replace(/(^|\s)\S/g, (ch) => ch.toUpperCase());
};

/** Facet destinations, duplicates by case merged (counts added), most packages first. */
export const facetDestinations = (facets) => {
  const map = new Map();
  (Array.isArray(facets?.destinations) ? facets.destinations : []).forEach(
    (d) => {
      const label = String(d?.label || '').trim();
      if (!label) return;
      const key = label.toLowerCase();
      const prev = map.get(key);
      const count = Number(d.count) || 0;
      if (prev) prev.count += count;
      else
        map.set(key, { label: prettyDestination(label), value: label, count });
    },
  );
  return [...map.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
};

/** Up to three round budget ceilings inside the catalogue's price range. */
export const budgetSteps = (facets) => {
  const lo = Number(facets?.priceMin);
  const hi = Number(facets?.priceMax);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return [];
  const candidates = [
    10000, 15000, 25000, 40000, 50000, 75000, 100000, 150000, 200000, 300000,
  ];
  return candidates.filter((c) => c > lo && c < hi).slice(-3);
};

/** The catalogue's day range from the facets, or the module default. */
export const durationBounds = (facets) => {
  const lo = Number(facets?.durationMin);
  const hi = Number(facets?.durationMax);
  return Number.isFinite(lo) && Number.isFinite(hi) && hi >= lo && lo >= 1
    ? { min: lo, max: hi }
    : { min: PACKAGE_LIMITS.MIN_DAYS, max: PACKAGE_LIMITS.MAX_DAYS };
};

// ---- reading packages --------------------------------------------------------

/** Absolute URL of a package image ({ baseUrl, imageUrl } or a string); '' when there is none. */
export const packageImageUrl = (img) => {
  if (typeof img === 'string') {
    return /^https?:\/\//i.test(img.trim()) ? img.trim() : '';
  }
  const path = String(img?.imageUrl || '').trim();
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(img?.baseUrl || '')
    .trim()
    .replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(base)) return '';
  return `${base}/${path.replace(/^\/+/, '')}`;
};

/** A review photo path ("/cdn/...") has no baseUrl of its own: borrow the package's. */
export const relativeImageUrl = (path, pkg) => {
  const p = String(path || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  return packageImageUrl({ imageUrl: p, baseUrl: pkg?.coverImage?.baseUrl });
};

/**
 * The supplier is internal information: only admin / admin-staff logins see
 * it. Everyone else gets '' so the card, details page, booking summary and
 * the "Supplier" filter (which needs 2+ options) all drop it on their own.
 */
export const canSeePackageSupplier = () => isAdminOrAdminStaff();

export const packageSupplier = (pkg) =>
  canSeePackageSupplier() ? pkg?.supplierName || '' : '';

/** Every distinct photo of one hotel row, as { url, caption } for the viewer. */
export const packageHotelImages = (hotel) => {
  const seen = new Set();
  return (Array.isArray(hotel?.images) ? hotel.images : [])
    .map((img) => ({
      url: packageImageUrl(img),
      caption: img?.description || '',
    }))
    .filter((img) => {
      if (!img.url || seen.has(img.url)) return false;
      seen.add(img.url);
      return true;
    });
};

/** The "from" price on search / detail: grandTotal of the lead fare (per person). */
export const packageFromPrice = (pkg) => {
  const n = num(pkg?.fare?.grandTotal);
  return n != null && n > 0 ? n : null;
};

export const packageCurrency = (pkg) => pkg?.fare?.currency || 'INR';
export const packageDays = (pkg) => num(pkg?.duration?.days);
export const packageNights = (pkg) => num(pkg?.duration?.nights);

export const packagePlace = (pkg) =>
  [prettyDestination(pkg?.destination?.label), pkg?.destination?.country]
    .filter(Boolean)
    .join(', ');

/** Every distinct image (cover first), as { url, caption } for the gallery. */
export const packageGallery = (pkg) => {
  const seen = new Set();
  return [pkg?.coverImage, ...(Array.isArray(pkg?.images) ? pkg.images : [])]
    .map((img) => ({
      url: packageImageUrl(img),
      caption: img?.description || '',
    }))
    .filter((img) => {
      if (!img.url || seen.has(img.url)) return false;
      seen.add(img.url);
      return true;
    });
};

const monthKeyOf = (date) =>
  date instanceof Date
    ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`
    : '';

// ---- results filters & sorts ---------------------------------------------------

export const EMPTY_PACKAGE_FILTERS = { duration: [], month: [], supplier: [] };

export const DURATION_BUCKETS = [
  { key: 'short', label: 'Up to 4 days', test: (d) => d != null && d <= 4 },
  {
    key: 'medium',
    label: '5 - 7 days',
    test: (d) => d != null && d >= 5 && d <= 7,
  },
  { key: 'long', label: '8+ days', test: (d) => d != null && d >= 8 },
];

const nextDepartureMonth = (pkg) => monthKeyOf(fromIsoDate(pkg?.nextDeparture));

/** The filter options this result set can actually narrow by, with counts. */
export const packageFilterOptions = (list) => {
  const months = new Map();
  const suppliers = new Map();
  list.forEach((p) => {
    const m = nextDepartureMonth(p);
    if (m) months.set(m, (months.get(m) || 0) + 1);
    const s = packageSupplier(p);
    if (s) suppliers.set(s, (suppliers.get(s) || 0) + 1);
  });
  return {
    duration: DURATION_BUCKETS.map((b) => ({
      key: b.key,
      label: b.label,
      count: list.filter((p) => b.test(packageDays(p))).length,
    })).filter((b) => b.count > 0),
    month: [...months.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ key, label: formatMonthKey(key), count })),
    supplier: [...suppliers.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ key, label: key, count })),
  };
};

export const applyPackageFilters = (list, f) =>
  list.filter(
    (p) =>
      (!f.duration.length ||
        DURATION_BUCKETS.some(
          (b) => f.duration.includes(b.key) && b.test(packageDays(p)),
        )) &&
      (!f.month.length || f.month.includes(nextDepartureMonth(p))) &&
      (!f.supplier.length || f.supplier.includes(packageSupplier(p))),
  );

export const countActiveFilters = (f) =>
  f.duration.length + f.month.length + f.supplier.length;

export const sortPackages = (list, key) => {
  const out = [...list];
  if (key === 'priceDesc')
    out.sort(
      (a, b) => (packageFromPrice(b) ?? -1) - (packageFromPrice(a) ?? -1),
    );
  else if (key === 'duration')
    out.sort(
      (a, b) => (packageDays(a) ?? Infinity) - (packageDays(b) ?? Infinity),
    );
  else if (key === 'departure')
    out.sort((a, b) =>
      String(a.nextDeparture || '9999').localeCompare(
        String(b.nextDeparture || '9999'),
      ),
    );
  else
    out.sort(
      (a, b) =>
        (packageFromPrice(a) ?? Infinity) - (packageFromPrice(b) ?? Infinity),
    );
  return out;
};

// ---- package detail ------------------------------------------------------------

const DEPARTURE_REASON_TEXT = {
  past: 'Departed',
  closed: 'Closed',
  seats: 'Not enough seats',
};

/**
 * Departures with dates as Dates. `bookable` is false for past, closed or
 * full departures (for `seats` people); `reason` says which.
 * Some suppliers send a returnDate before the departure (seen in the sample
 * data); then the return is worked out from the package's nights.
 */
export const packageDepartures = (pkg, seats = 1, today = startOfToday()) => {
  const nights = packageNights(pkg);
  return (Array.isArray(pkg?.departures) ? pkg.departures : [])
    .filter((d) => d?.departureKey && fromIsoDate(d.departureDate))
    .map((d) => {
      const date = fromIsoDate(d.departureDate);
      let returnDate = fromIsoDate(d.returnDate);
      if (!returnDate || returnDate < date)
        returnDate = nights != null ? addDays(date, nights) : null;
      const available = num(d.seatsAvailable);
      const open = String(d.status || 'open').toLowerCase() === 'open';
      let reason = '';
      if (date <= today) reason = 'past';
      else if (!open) reason = 'closed';
      else if (available != null && available < seats) reason = 'seats';
      return {
        key: d.departureKey,
        id: d.departureId,
        date,
        returnDate,
        seatsAvailable: available,
        guaranteed: Boolean(Number(d.guaranteed)),
        seasonLabel: d.seasonLabel || '',
        bookable: !reason,
        reason,
        reasonText: DEPARTURE_REASON_TEXT[reason] || '',
      };
    })
    .sort((a, b) => a.date - b.date);
};

/** The grade a price / hotel row belongs to; null means the package's first (base) grade. */
export const gradeLabel = (grade, pkg) =>
  grade || (Array.isArray(pkg?.grades) && pkg.grades[0]) || '';

/**
 * Price options (joining city × grade), each with its per-person rate by
 * sharing type: { priceKey, city, cityId, grade, currency, rates: { twin: 7999, … } }.
 */
export const packagePriceOptions = (pkg) =>
  (Array.isArray(pkg?.prices) ? pkg.prices : [])
    .filter((p) => p?.priceKey)
    .map((p) => ({
      priceKey: p.priceKey,
      city: p.joiningCityLabel || '',
      cityId: p.joiningCityId,
      grade: p.grade || null,
      currency: p.currency || 'INR',
      rates: (Array.isArray(p.breakdown) ? p.breakdown : []).reduce(
        (acc, b) => {
          const rate = num(b?.pricePp);
          if (b?.roomType && rate != null) acc[b.roomType] = rate;
          return acc;
        },
        {},
      ),
    }));

/** The lowest adult rate of an option, for the "from" line on each option. */
export const optionLeadRate = (option) => {
  if (!option) return null;
  if (option.rates.twin != null) return option.rates.twin;
  const adult = ['single', 'triple']
    .map((k) => option.rates[k])
    .filter((v) => v != null && v > 0);
  return adult.length ? Math.min(...adult) : null;
};

/** Sharing types the chosen price offers (a missing rate means not sold). */
export const offeredPaxTypes = (option) =>
  PACKAGE_PAX_TYPES.filter((t) => option && option.rates[t.key] != null);

/**
 * pax with the sharing types `option` doesn't sell set to 0. Returns the same
 * object when nothing changes, so it is safe inside a state updater.
 */
export const paxForOption = (pax, option) => {
  if (!option) return pax;
  const unsold = Object.keys(pax || {}).filter(
    (k) => Number(pax[k]) > 0 && option.rates[k] == null,
  );
  if (!unsold.length) return pax;
  const next = { ...pax };
  unsold.forEach((k) => {
    next[k] = 0;
  });
  return next;
};

/** Per-person rates × people. null when a chosen sharing type has no rate. */
export const estimatePackageTotal = (option, pax) => {
  if (!option) return null;
  const p = normalizePax(pax);
  let total = 0;
  const missing = PACKAGE_PAX_TYPES.some((t) => {
    if (p[t.key] <= 0) return false;
    const rate = option.rates[t.key];
    if (rate == null) return true;
    total += rate * p[t.key];
    return false;
  });
  return missing ? null : total;
};

/** Hotels for a grade (rows without a grade belong to the base grade). */
export const hotelsForGrade = (pkg, grade) => {
  const hotels = Array.isArray(pkg?.hotels) ? pkg.hotels.filter(Boolean) : [];
  const base = gradeLabel(null, pkg);
  const wanted = grade || base;
  const matching = hotels.filter((h) => (h.grade || base) === wanted);
  return matching.length ? matching : hotels.filter((h) => !h.grade);
};

/** Flight legs for a joining city, outbound first. */
export const flightsForCity = (pkg, city) => {
  const flights = Array.isArray(pkg?.flights)
    ? pkg.flights.filter(Boolean)
    : [];
  const list = city
    ? flights.filter((f) => !f.joiningCityLabel || f.joiningCityLabel === city)
    : flights;
  const order = (f) =>
    String(f.legGroup || '').toLowerCase() === 'outbound' ? 0 : 1;
  return [...list].sort((a, b) => order(a) - order(b));
};

export const packageSections = (pkg) =>
  (Array.isArray(pkg?.sections) ? pkg.sections : [])
    .filter((s) => s?.title && s.body)
    .sort((a, b) => (num(a.sortOrder) ?? 0) - (num(b.sortOrder) ?? 0));

/** Supplier section titles -> tone: what's in, what's not, everything else. */
export const sectionTone = (title) => {
  const t = String(title || '').toLowerCase();
  if (/exclu|not included|not cover/.test(t)) return 'out';
  if (/inclu|cover/.test(t)) return 'in';
  return 'info';
};

/** Itinerary days in order. */
export const packageItinerary = (pkg) =>
  (Array.isArray(pkg?.days) ? pkg.days : [])
    .filter(Boolean)
    .sort((a, b) => (num(a.dayNumber) ?? 0) - (num(b.dayNumber) ?? 0));

export const MEALS = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
];

// ---- reviews & videos ------------------------------------------------------------

/**
 * { average, count, breakdown: [{ star, count }] (5 → 1), list: [{ id, name,
 *   rating, title, text, date, verified, images }] }. null when there is nothing.
 */
export const packageReviews = (pkg) => {
  const r = pkg?.reviews || {};
  const s = r.summary || {};
  const list = (Array.isArray(r.results) ? r.results : [])
    .filter((x) => x && typeof x === 'object')
    .map((x, i) => ({
      id: x.reviewId ?? `r${i}`,
      name: String(x.reviewerName || '').trim(),
      rating: num(x.rating),
      title: String(x.title || '').trim(),
      text: String(x.body || '').trim(),
      date: x.experienceDate || x.createdon || '',
      verified: Boolean(Number(x.isVerified)),
      reply: String(x.operatorReply || '').trim(),
      images: (Array.isArray(x.photos) ? x.photos : [])
        .map((p) =>
          typeof p === 'string' ? relativeImageUrl(p, pkg) : packageImageUrl(p),
        )
        .filter(Boolean),
    }))
    .filter((x) => x.text || x.rating != null || x.images.length);
  const breakdown = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: num(s.breakdown?.[star] ?? s.breakdown?.[String(star)]) || 0,
  }));
  const counted = breakdown.reduce((n, b) => n + b.count, 0);
  const count = num(s.ratingCount) || counted || list.length;
  if (!count && !list.length) return null;
  const bars = counted
    ? breakdown
    : breakdown.map((b) => ({
        ...b,
        count: list.filter((x) => Math.round(x.rating || 0) === b.star).length,
      }));
  const rated = list.filter((x) => x.rating != null);
  const average =
    num(s.ratingAvg) ||
    (rated.length
      ? rated.reduce((n, x) => n + x.rating, 0) / rated.length
      : null);
  return { average, count, breakdown: bars, list };
};

const YOUTUBE_RE =
  /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d{6,12})/i;
const FILE_RE = /\.(mp4|webm|ogg|mov)(\?|#|$)/i;

/**
 * Videos as { key, kind: 'youtube' | 'vimeo' | 'file' | 'link', url, embedUrl,
 * thumbnail, title }. The sample package has none, so the item shape is read
 * leniently: a plain URL string, or an object with url / videoUrl / imageUrl.
 * Only https links are kept.
 */
export const packageVideos = (pkg) => {
  const seen = new Set();
  return (Array.isArray(pkg?.videos) ? pkg.videos : [])
    .map((v) => {
      let raw = '';
      if (typeof v === 'string') raw = v;
      else if (v && typeof v === 'object')
        raw = v.videoUrl || v.url || v.link || packageImageUrl(v) || v.embedUrl;
      const url = String(raw || '').trim();
      if (!/^https:\/\//i.test(url)) return null;
      const title = (typeof v === 'object' && (v.title || v.description)) || '';
      const yt = YOUTUBE_RE.exec(url);
      if (yt)
        return {
          kind: 'youtube',
          url,
          embedUrl: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`,
          thumbnail: `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg`,
          title,
        };
      const vm = VIMEO_RE.exec(url);
      if (vm)
        return {
          kind: 'vimeo',
          url,
          embedUrl: `https://player.vimeo.com/video/${vm[1]}?autoplay=1`,
          thumbnail: '',
          title,
        };
      return {
        kind: FILE_RE.test(url) ? 'file' : 'link',
        url,
        embedUrl: '',
        thumbnail: '',
        title,
      };
    })
    .filter((v) => {
      const id = v?.embedUrl || v?.url;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((v) => ({ ...v, key: v.embedUrl || v.url }));
};

// ---- book ------------------------------------------------------------------------

/** FSPB<time><random>: unique per attempt, readable in support tickets. */
export const newClientBookingId = () => {
  const time = Date.now().toString(36).toUpperCase();
  let random = '';
  try {
    const bytes = new Uint8Array(4);
    window.crypto.getRandomValues(bytes);
    random = [...bytes].map((b) => b.toString(36).padStart(2, '0')).join('');
  } catch (e) {
    random = Math.random().toString(36).slice(2, 10);
  }
  return `FSPB${time}${random.toUpperCase()}`.slice(0, 40);
};

/** The API's own spelling (see the collection): "male" / "female". */
export const genderForTitle = (title) =>
  ['Mr', 'Mstr'].includes(title) ? 'male' : 'female';

/** ADULT -> "Adult", as the collection sends passengerType. */
const apiPassengerType = (type) =>
  PASSENGER_TYPE_LABEL[type] || PASSENGER_TYPE_LABEL.ADULT;

/** National number for India (as in the collection), dial code + number otherwise. */
const apiPhone = (dialCode, mobile) =>
  dialCode === '+91' ? mobile : `${dialCode}${mobile}`;

/** The book API's spellings: "Male" / "Female", and "Master" for a boy. */
const bookGender = (title) =>
  ['Mr', 'Mstr', 'Master'].includes(title) ? 'Male' : 'Female';
const bookTitle = (title) => (title === 'Mstr' ? 'Master' : title);

/**
 * The book body:
 *   { packageKey, clientBookingId, searchId, nextraQuoteId, priceKey,
 *     departureKey, pax: { adult, child, infant }, passengers,
 *     specialRequests, payment: { paymentMode, pgName } }
 *
 * The room-sharing mix is already fixed by the quote (nextraQuoteId), so the
 * book call only counts people. Every passenger carries the same keys; only
 * the lead has an email and phone. pgName is '' for wallet, as on the
 * eSIM / visa bookings.
 *
 * passengers: [{ type, title, firstName, lastName, age, email, dialCode, mobile }]
 */
export const buildPackageBookRequest = ({
  clientBookingId,
  ctx,
  passengers,
  specialRequests,
  paymentMode,
  pgName,
}) => {
  const { quote } = ctx;
  return {
    packageKey: quote.packageKey || ctx.packageKey,
    clientBookingId,
    searchId: ctx.searchId || '',
    nextraQuoteId: quote.nextraQuoteId,
    priceKey: quote.fare?.priceKey || ctx.priceKey,
    departureKey: quote.departureKey || ctx.departureKey,
    pax: {
      adult: adultsOf(ctx.pax),
      child: childrenOf(ctx.pax),
      infant: infantsOf(ctx.pax),
    },
    passengers: passengers.map((p, i) => {
      const lead = i === 0;
      return {
        title: bookTitle(p.title),
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim(),
        email: lead ? p.email.trim() : null,
        phone: lead ? apiPhone(p.dialCode, p.mobile) : null,
        gender: bookGender(p.title),
        age: Number(p.age),
        passengerType: apiPassengerType(p.type),
        isLead: lead,
        message: null,
      };
    }),
    specialRequests: String(specialRequests || '')
      .trim()
      .slice(0, PACKAGE_LIMITS.MAX_REQUESTS),
    payment: {
      paymentMode,
      pgName: paymentMode === 'cashcard' ? '' : pgName || '',
    },
  };
};

/**
 * Where to send the agent to pay, from a book result for an online payment.
 * The packages API hasn't documented this yet, so the names the other
 * services use are read (PgUrl + Txid, as eSIM / visa do). '' when absent.
 */
export const paymentRedirectUrl = (result) => {
  if (!result || typeof result !== 'object') return '';
  const url = String(
    result.PgUrl ||
      result.pgUrl ||
      result.paymentUrl ||
      result.redirectUrl ||
      '',
  ).trim();
  if (!/^https?:\/\//i.test(url)) return '';
  const txid = result.Txid || result.txid || '';
  if (!txid || /[?&]txid=/i.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}txid=${encodeURIComponent(
    txid,
  )}`;
};

/** The enquiry body, in the shape of the API collection: the lead carries the message. */
export const buildPackageEnquiryRequest = ({
  packageKey,
  searchId,
  lead,
  message,
}) => ({
  packageKey,
  clientBookingId: newClientBookingId(),
  searchId: searchId || '',
  passengers: [
    {
      title: lead.title,
      firstName: lead.firstName.trim(),
      lastName: lead.lastName.trim(),
      email: lead.email.trim(),
      phone: apiPhone(lead.dialCode, lead.mobile),
      gender: genderForTitle(lead.title),
      age: Number(lead.age),
      passengerType: apiPassengerType('ADULT'),
      isLead: true,
      message: String(message || '')
        .trim()
        .slice(0, PACKAGE_LIMITS.MAX_REQUESTS),
    },
  ],
});

// ---- validation ------------------------------------------------------------------

const NAME_RE = /^[A-Za-z][A-Za-z .'-]*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Keep letters, space and . ' - only. */
export const sanitizeName = (value, max = 40) =>
  String(value || '')
    .replace(/[^A-Za-z .'-]/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+/, '')
    .slice(0, max);

export const sanitizeDigits = (value, max) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, max);

export const nameError = (value, label) => {
  const v = String(value || '').trim();
  if (!v) return `${label} is required`;
  if (!NAME_RE.test(v)) return `${label} can only have letters`;
  if (v.length < 2) return `${label} is too short`;
  return '';
};

export const ageError = (value, type) => {
  const limits = PACKAGE_AGE_LIMITS[type] || PACKAGE_AGE_LIMITS.ADULT;
  if (value === '' || value === null || value === undefined)
    return 'Age is required';
  const age = Number.parseInt(value, 10);
  if (!Number.isFinite(age) || age < limits.min || age > limits.max)
    return `${PASSENGER_TYPE_LABEL[type] || 'Adult'} age must be ${
      limits.min
    } - ${limits.max} years`;
  return '';
};

export const emailError = (value) => {
  const v = String(value || '').trim();
  if (!v) return 'Email is required';
  return EMAIL_RE.test(v) ? '' : 'Enter a valid email address';
};

/** India: 10 digits starting 6-9. Elsewhere: 6 - 14 digits. */
export const mobileError = (value, dialCode) => {
  const v = String(value || '');
  if (!v) return 'Mobile number is required';
  if (dialCode === '+91')
    return /^[6-9]\d{9}$/.test(v) ? '' : 'Enter a valid 10 digit mobile number';
  return /^\d{6,14}$/.test(v) ? '' : 'Enter a valid mobile number';
};

/**
 * Errors for a list of passenger forms, keyed `p{index}-{field}` (the
 * PackagePassengerForm convention). The first passenger is the lead and must
 * also give an email and mobile.
 */
export const validatePassengers = (passengers) => {
  const errors = {};
  passengers.forEach((p, i) => {
    const put = (field, message) => {
      if (message) errors[`p${i}-${field}`] = message;
    };
    put('title', p.title ? '' : 'Select a title');
    put('first', nameError(p.firstName, 'First name'));
    put('last', nameError(p.lastName, 'Last name'));
    put('age', ageError(p.age, p.type));
    if (i === 0) {
      put('email', emailError(p.email));
      put('mobile', mobileError(p.mobile, p.dialCode));
    }
  });
  return errors;
};

// ---- booking status --------------------------------------------------------------

export const packageStatusOf = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const PENDING = [
  'pending',
  'processing',
  'onhold',
  'on_hold',
  'on hold',
  'requested',
  'inprogress',
];
const FAILED = [
  'failed',
  'rejected',
  'cancelled',
  'canceled',
  'rolledback',
  'rolled_back',
];

export const isPackagePending = (status) =>
  PENDING.includes(packageStatusOf(status));
export const isPackageFailed = (status) =>
  FAILED.includes(packageStatusOf(status));
export const isPackageEnquiry = (status) =>
  packageStatusOf(status).startsWith('enquiry');

/**
 * booking-details Result -> one flat booking with the package under `info`.
 * The response nests the booking in `packageMain` and the package in
 * `packageMain.packageInfo`.
 */
export const readPackageBooking = (result) => {
  if (!result || typeof result !== 'object') return null;
  const main =
    result.packageMain && typeof result.packageMain === 'object'
      ? result.packageMain
      : result;
  if (!main.pkgBookingId && !main.clientBookingId) return null;
  const info =
    main.packageInfo && typeof main.packageInfo === 'object'
      ? main.packageInfo
      : null;
  return { ...main, info };
};

/** booking-details pax columns (paxTwin, paxChildWithBed…) -> sharing-type keys. */
export const BOOKING_PAX_COLUMNS = {
  twin: 'paxTwin',
  single: 'paxSingle',
  triple: 'paxTriple',
  child_with_bed: 'paxChildWithBed',
  child_no_bed: 'paxChildNoBed',
  infant: 'paxInfant',
};

/** pkgBookingId, e.g. PKGB202610161792147774127234. Lenient on purpose. */
export const PACKAGE_REF_RE = /^[A-Za-z0-9-]{6,40}$/;
